import { useEffect, useRef, useState } from 'react';

/**
 * Animates the returned number toward `target` (ease-out cubic, ~450ms), starting from wherever
 * the previous animation left off — so a retarget mid-flight doesn't jump. Values are rounded to
 * whole numbers (these are levels). Interval-driven (~60fps) rather than requestAnimationFrame:
 * visually identical for a number readout, and it also runs under jsdom, where RAF callbacks
 * don't fire.
 */
export function useTweenedNumber(target: number, duration = 450): number {
  const [value, setValue] = useState(target);
  const valueRef = useRef(target);

  useEffect(() => {
    const from = valueRef.current;
    if (from === target) {
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + (target - from) * eased);
      valueRef.current = next;
      setValue(next);
      if (t >= 1) {
        clearInterval(id);
      }
    }, 16);
    return () => clearInterval(id);
  }, [target, duration]);

  return value;
}
